import api from './api';

// Send Survey API Service
export const sendSurveyService = {
  // Get companies (filtered by user role)
  getCompanies: () => {
    return api.get('/SendSurvey/companies');
  },

  // Get branches by company
  getBranches: (companyId) => {
    return api.get(`/SendSurvey/branches/${companyId}`);
  },

  // Get departments by branch
  getDepartments: (branchId) => {
    return api.get(`/SendSurvey/departments/${branchId}`);
  },

  // Get divisions by department
  getDivisions: (departmentId) => {
    return api.get(`/SendSurvey/divisions/${departmentId}`);
  },

  // Get employees with filters
  getEmployees: (filters = {}) => {
    const params = new URLSearchParams();
    
    // Add filter parameters if provided
    if (filters.companyId) params.append('companyId', filters.companyId);
    if (filters.branchId) params.append('branchId', filters.branchId);
    if (filters.departmentId) params.append('departmentId', filters.departmentId);
    if (filters.divisionId) params.append('divisionId', filters.divisionId);
    if (filters.searchTerm) params.append('searchTerm', filters.searchTerm);

    return api.get(`/SendSurvey/employees?${params}`);
  },

  // Get available surveys (filtered by user role)
  getSurveys: () => {
    return api.get('/SendSurvey/surveys');
  },

  // Send survey to selected employees - FIXED VERSION
  // Send survey to selected employees - URGENT FIX
sendSurvey: async (sendData) => {
  console.log('🔄 Original sendData:', sendData);

  // SIMPLIFIED DATA PROCESSING - Remove all complex validation for now
  const processedData = {
    surveyId: parseInt(sendData.surveyId, 10),
    employeeIds: sendData.employeeIds.map(id => parseInt(id, 10)),
    baseUrl: sendData.baseUrl,
    estimatedTime: parseInt(sendData.estimatedTime, 10) || 10,
    deadline: sendData.deadline || null,
    message: sendData.message || ''
  };

  console.log('📤 Processed data:', processedData);
  console.log('🔍 Data types - ACTUAL:', {
    surveyId: typeof processedData.surveyId,
    employeeIds: processedData.employeeIds.map(id => ({ value: id, type: typeof id })),
    baseUrl: typeof processedData.baseUrl
  });

  // Validate minimal requirements
  if (!processedData.surveyId || processedData.surveyId <= 0) {
    throw new Error('Valid surveyId is required');
  }

  if (!processedData.employeeIds || processedData.employeeIds.length === 0) {
    throw new Error('At least one employee must be selected');
  }

  if (!processedData.baseUrl) {
    throw new Error('baseUrl is required');
  }

  try {
    console.log('📨 Making API request...');
    
    const response = await api.post('/SendSurvey/send', processedData, {
      headers: {
        'Content-Type': 'application/json',
      },
      // NOTE: the server sends each selected employee's invitation email
      // synchronously, one by one, before it responds -- so this timeout has
      // to scale with how many employees were selected, not just be "long
      // enough for one request". 30s was fine when SMTP sending was silently
      // failing instantly (pre bug-fix), but now that real emails actually
      // go out, a batch of dozens of employees can legitimately take longer
      // than that. A short timeout here doesn't mean the send failed -- see
      // the comment on the caller in SendSurvey.jsx about why retrying on
      // timeout is unsafe for this non-idempotent endpoint.
      timeout: 120000
    });

    console.log('✅ API Response received:', response.data);
    return response;
  } catch (error) {
    console.error('❌ API Error:', error);
    console.error('❌ Error response:', error.response?.data);
    console.error('❌ Error status:', error.response?.status);
    throw error;
  }
},

  // Validate survey access (for encoded links)
  validateSurveyAccess: (surveyId, token, encodedEmployeeId) => {
    return api.get(`/SendSurvey/validate/${surveyId}`, {
      params: { token, eid: encodedEmployeeId }
    });
  },

  // Get survey invitation status
  getInvitationStatus: (surveyId, employeeId) => {
    return api.get(`/SendSurvey/invitation-status/${surveyId}/${employeeId}`);
  },

  // Resend survey invitation
  resendInvitation: (invitationId) => {
    return api.post(`/SendSurvey/resend/${invitationId}`);
  },

  // Get sending statistics
  getSendingStats: (surveyId) => {
    return api.get(`/SendSurvey/stats/${surveyId}`);
  },

  // Bulk operations
  bulkSend: (sendDataArray) => {
    return api.post('/SendSurvey/bulk-send', sendDataArray);
  },

  // Get email templates for survey invitations
  getSurveyEmailTemplates: () => {
    return api.get('/SendSurvey/email-templates');
  },

  // Test email configuration
  testEmailConfiguration: (testData) => {
    return api.post('/SendSurvey/test-email', testData);
  }
};

// Enhanced methods with error handling and retry logic
export const enhancedSendSurveyService = {
  // Copy all methods from sendSurveyService
  ...sendSurveyService,

  // WARNING: do not use this for POST /SendSurvey/send (see the comment in
  // SendSurvey.jsx's handleSendSurvey). That endpoint creates a new
  // SurveyInvitation row and sends a real email per employee on every call,
  // with no idempotency key -- retrying it on a timeout re-sends the whole
  // batch again rather than safely resuming. This helper is only safe to use
  // against an endpoint that's actually idempotent.
  // Send survey with retry logic
  sendSurveyWithRetry: async (sendData, maxRetries = 3) => {
    let lastError;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`📤 Attempt ${attempt} to send survey...`);
        const response = await sendSurveyService.sendSurvey(sendData);
        console.log(`✅ Survey sent successfully on attempt ${attempt}`);
        return response;
      } catch (error) {
        lastError = error;
        console.warn(`⚠️ Attempt ${attempt} failed:`, error.message);
        
        if (attempt < maxRetries) {
          // Exponential backoff
          const delay = Math.min(1000 * Math.pow(2, attempt), 10000);
          console.log(`⏳ Retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    throw lastError;
  },

  // Send survey in chunks to avoid timeout
  sendSurveyInChunks: async (sendData, chunkSize = 50) => {
    const { employeeIds, ...restData } = sendData;
    const chunks = [];
    
    // Split employee IDs into chunks
    for (let i = 0; i < employeeIds.length; i += chunkSize) {
      chunks.push(employeeIds.slice(i, i + chunkSize));
    }

    const results = {
      totalSent: 0,
      totalFailed: 0,
      chunkResults: []
    };

    console.log(`📦 Sending survey in ${chunks.length} chunks of ${chunkSize} employees each`);

    for (let i = 0; i < chunks.length; i++) {
      try {
        console.log(`📤 Processing chunk ${i + 1}/${chunks.length}...`);
        const chunkData = {
          ...restData,
          employeeIds: chunks[i]
        };

        const response = await sendSurveyService.sendSurvey(chunkData);
        results.totalSent += response.data.totalSent;
        results.totalFailed += response.data.totalFailed;
        results.chunkResults.push({
          chunk: i + 1,
          success: true,
          sent: response.data.totalSent,
          failed: response.data.totalFailed,
          results: response.data.results
        });

        console.log(`✅ Chunk ${i + 1} completed: ${response.data.totalSent} sent, ${response.data.totalFailed} failed`);

        // Small delay between chunks to avoid overwhelming the server
        if (i < chunks.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      } catch (error) {
        console.error(`❌ Chunk ${i + 1} failed:`, error.message);
        results.chunkResults.push({
          chunk: i + 1,
          success: false,
          error: error.message,
          sent: 0,
          failed: chunks[i].length
        });
        results.totalFailed += chunks[i].length;
      }
    }

    return { data: results };
  },

  // Validate and prepare send data
  validateSendData: (sendData) => {
    const errors = [];

    if (!sendData.surveyId) {
      errors.push('Survey ID is required');
    }

    if (!sendData.employeeIds || !Array.isArray(sendData.employeeIds) || sendData.employeeIds.length === 0) {
      errors.push('At least one employee must be selected');
    }

    if (!sendData.baseUrl) {
      errors.push('Base URL is required for survey links');
    }

    if (sendData.estimatedTime && (sendData.estimatedTime < 1 || sendData.estimatedTime > 120)) {
      errors.push('Estimated time must be between 1 and 120 minutes');
    }

    if (sendData.deadline) {
      const deadlineDate = new Date(sendData.deadline);
      if (deadlineDate <= new Date()) {
        errors.push('Deadline must be in the future');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  },

  // Generate preview of email that will be sent
  generateEmailPreview: async (sendData) => {
    try {
      // This would typically call a backend endpoint to generate email preview
      const template = {
        subject: `Survey Invitation - ${sendData.surveyName || 'Unknown Survey'}`,
        body: `
          <h2>Survey Invitation</h2>
          <p>You have been invited to participate in a survey.</p>
          <p><strong>Survey:</strong> ${sendData.surveyName || 'Unknown'}</p>
          <p><strong>Estimated Time:</strong> ${sendData.estimatedTime || 10} minutes</p>
          ${sendData.deadline ? `<p><strong>Deadline:</strong> ${new Date(sendData.deadline).toLocaleDateString()}</p>` : ''}
          ${sendData.message ? `<p><strong>Additional Message:</strong> ${sendData.message}</p>` : ''}
          <p><em>Note: Each employee will receive a unique survey link with encoded identifier for security.</em></p>
        `
      };

      return { data: template };
    } catch (error) {
      console.error('Error generating email preview:', error);
      throw error;
    }
  }
};

export default sendSurveyService;